import React, { useEffect, useRef, useState } from 'react';
import {
  ActionList,
  ActionListGroup,
  ActionListItem,
  Button,
  ButtonVariant,
  Flex,
  FlexItem,
  Modal,
  ModalBody,
  Title
} from '@patternfly/react-core';
import useLogger from 'src/hooks/useLogger';
import useWebSocket from 'src/hooks/useWebSocket';

interface BotControlModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoRef: React.RefObject<HTMLVideoElement>;
  client: {
    id: string;
    name: string;
    status: string;
    telemetry: {
      ip: string;
      battery: number;
    };
  };
}

const BotControlModal: React.FC<BotControlModalProps> = ({ isOpen, onClose, client, videoRef }) => {
  const { logInfo } = useLogger();
  const { sendMessage } = useWebSocket();
  const [mode, setMode] = useState('Teleop');
  const modalVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (isOpen && videoRef.current && modalVideoRef.current) {
      const sourceStream = videoRef.current.srcObject;
      if (sourceStream) {
        logInfo(`Copying WebRTC stream for bot ${client.id} to modal`);
        modalVideoRef.current.srcObject = sourceStream;
      } else {
        logInfo(`No stream found for bot ${client.id}`);
      }
    }
  }, [isOpen, videoRef, client.id, logInfo]);

  const handleModeChange = (newMode: string) => {
    logInfo(`Changing mode to ${newMode} for bot ${client.id}`);
    setMode(newMode.charAt(0).toUpperCase() + newMode.slice(1));

    sendMessage({
      type: 'mode-change',
      mode: newMode,
      botId: client.id
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      id={client.name}
      variant="large"
    >
      <ModalBody>
        <Flex direction={{ default: 'column' }} gap={{ default: 'gapMd' }}>
          <FlexItem>
            <Title headingLevel="h3" >Live View</Title>
            <div style={{ height: '300px', marginBottom: '20px' }}>
              <video
                ref={modalVideoRef}
                autoPlay
                playsInline
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
          </FlexItem>

          <FlexItem>
            <Title headingLevel="h3">Status Information</Title>
            <p><strong>Status:</strong> {client.status}</p>
            <p><strong>IP:</strong> {client.telemetry.ip}</p>
            <p><strong>Battery:</strong> {client.telemetry.battery}%</p>
            <p><strong>Current Mode:</strong> {mode}</p>
          </FlexItem>

          <FlexItem>
            <Title headingLevel="h3">Mode Controls</Title>
            <ActionList>
              <ActionListGroup>
                <ActionListItem>
                  <Button onClick={() => handleModeChange('teleop')} variant={mode === 'Teleop' ? ButtonVariant.primary : ButtonVariant.secondary}>
                    Teleop
                  </Button>
                </ActionListItem>
                <ActionListItem>
                  <Button onClick={() => handleModeChange('home')} variant={mode === 'Home' ? ButtonVariant.primary : ButtonVariant.secondary}>
                    Home
                  </Button>
                </ActionListItem>
                <ActionListItem>
                  <Button onClick={() => handleModeChange('sleep')} variant={mode === 'Sleep' ? ButtonVariant.primary : ButtonVariant.secondary}>
                    Sleep
                  </Button>
                </ActionListItem>
              </ActionListGroup>
            </ActionList>
          </FlexItem>

        </Flex>
      </ModalBody>
    </Modal>
  );
};

export default BotControlModal;
